import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 30000 });

// Reuse the same token storage as csuiteApi — single sign-on.
const TOKEN_KEY = "pmv5_csuite_token";

client.interceptors.request.use((cfg) => {
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
  } catch (e) { /* ignore */ }
  return cfg;
});

// ─── Cold-start auto-retry ───────────────────────────────────────────
// On Emergent production the backend pod sleeps after idle. The first
// request after a sleep returns 5xx (520 from Cloudflare, 502/503/504
// from the ingress) while the container spins up. Retry transparently
// with exponential backoff so the user never sees a cold-start error.
const RETRYABLE = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const MAX_RETRIES = 4;

client.interceptors.response.use(
  (r) => r,
  async (e) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    const cfg = e?.config || {};

    if (status === 401 && /revoked|expired|invalid|not authenticated/i.test(detail || "")) {
      try { localStorage.removeItem(TOKEN_KEY); } catch (x) { /* ignore */ }
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        // C-Suite routes → C-Suite login; everything else → operator login.
        if (path.startsWith("/csuite")) {
          if (!path.startsWith("/csuite") || path === "/csuite/dashboard" || path.startsWith("/csuite/project")) {
            window.location.assign("/csuite?next=" + encodeURIComponent(path));
          }
        } else if (path !== "/login" && path !== "/") {
          window.location.assign("/login?next=" + encodeURIComponent(path));
        }
      }
      return Promise.reject(e);
    }

    // Retry on cold-start / transient gateway errors AND on network errors
    // (axios surfaces these with no response object).
    const networkError = !e.response && (e.code === "ECONNABORTED" || e.code === "ERR_NETWORK" || /network/i.test(e.message || ""));
    const isRetryable = RETRYABLE.has(status) || networkError;

    cfg.__retryCount = cfg.__retryCount || 0;
    if (isRetryable && cfg.__retryCount < MAX_RETRIES && (cfg.method || "get").toLowerCase() === "get") {
      cfg.__retryCount += 1;
      const wait = Math.min(800 * Math.pow(2, cfg.__retryCount - 1), 6000);
      // Notify the UI (optional listener)
      try { window.dispatchEvent(new CustomEvent("api:warming", { detail: { attempt: cfg.__retryCount, wait } })); } catch (x) { /* ignore */ }
      await new Promise(res => setTimeout(res, wait));
      return client(cfg);
    }

    return Promise.reject(e);
  },
);

// Fire-and-forget warm-up — call once on app boot so the backend pod is
// awake by the time the user actually navigates into the workspace.
export const warmBackend = () => {
  client.get("/", { timeout: 8000, __retryCount: MAX_RETRIES }).catch(() => {});
};

export const api = {
  list:      ()                => client.get("/projects").then(r => r.data),
  get:       (id)              => client.get(`/projects/${id}`).then(r => r.data),
  create:    (payload)         => client.post("/projects", payload).then(r => r.data),
  update:    (id, payload)     => client.put(`/projects/${id}`, payload).then(r => r.data),
  remove:    (id)              => client.delete(`/projects/${id}`).then(r => r.data),
  duplicate: (id)              => client.post(`/projects/${id}/duplicate`).then(r => r.data),
  import:    (id, doc)         => client.post(`/projects/${id}/import`, doc).then(r => r.data),
  share:     (id, ttlDays=7)   => client.post(`/projects/${id}/share`, { ttl_days: ttlDays }).then(r => r.data),
  getShared: (id, token)       => client.get(`/projects/${id}/shared`, { params: { token } }).then(r => r.data),
  notificationsScan:     (id) => client.post(`/projects/${id}/notifications/scan`).then(r => r.data),
  notificationsDispatch: (id) => client.post(`/projects/${id}/notifications/dispatch`).then(r => r.data),
  notificationsTest:     (id) => client.post(`/projects/${id}/notifications/test`).then(r => r.data),
  subscribeCheckout:     (plan, nextTarget = "") => client.post(`/subscribe/checkout`, { plan, origin_url: window.location.origin, next: nextTarget }).then(r => r.data),
  subscribeStatus:       (sid)  => client.get(`/subscribe/status/${sid}`).then(r => r.data),
  subscribeCancel:       ()     => client.post(`/subscribe/cancel`).then(r => r.data),
  adminSubscribers:      ()     => client.get(`/admin/subscribers`).then(r => r.data),
  contact: (payload) => client.post(`/support/contact`, payload).then(r => r.data),
  // ─── Enterprise ─────────────────────────────────────────────────────
  resetProject:     (pid)   => client.post(`/projects/${pid}/reset`).then(r => r.data),
  resetAllProjects: ()      => client.post(`/projects/reset-all`).then(r => r.data),
  getOverride:      ()      => client.get(`/csuite/dashboard-override`).then(r => r.data),
  putOverride:      (o)     => client.put(`/csuite/dashboard-override`, o).then(r => r.data),
  clearOverride:    ()      => client.delete(`/csuite/dashboard-override`).then(r => r.data),
  listInitiatives:  ()      => client.get(`/csuite/initiatives`).then(r => r.data),
  importInitiatives:(keys)  => client.post(`/csuite/initiatives/import`, { keys }).then(r => r.data),
  pilotAnalytics:   ()      => client.get(`/admin/pilot-analytics`).then(r => r.data),
  myTemplates:      ()      => client.get(`/templates/mine`).then(r => r.data),
  templateCheckout: (origin_url = window.location.origin) => client.post(`/templates/checkout`, { origin_url }).then(r => r.data),
  templateStatus:   (session_id) => client.get(`/templates/checkout/status`, { params: { session_id } }).then(r => r.data),
  deleteTemplate:   (filename) => client.delete(`/templates/${encodeURIComponent(filename)}`).then(r => r.data),
  templateDownload: (kind)     => client.get(`/templates/download/${kind}`).then(r => r.data).catch(() => null),
  trackPoster:      (payload)  => client.post(`/analytics/poster`, payload).then(r => r.data),
  posterAnalytics:  ()         => client.get(`/admin/poster-analytics`).then(r => r.data),
  portfolioOverview: ()        => client.get(`/portfolio/overview`).then(r => r.data),
  switchOptions:     ()        => client.get(`/subscribe/switch-options`).then(r => r.data),
  switchPlan:        (payload) => client.post(`/subscribe/switch`, payload).then(r => r.data),
  projectDiagnostics:(id)      => client.get(`/projects/${id}/diagnostics`).then(r => r.data),
  portfolioDiagnostics:()      => client.get(`/portfolio/diagnostics`).then(r => r.data),
  remediationDraft:  (id)      => client.post(`/projects/${id}/diagnostics/remediation-draft`).then(r => r.data),
  remediationAccept: (id, items) => client.post(`/projects/${id}/diagnostics/remediation-accept`, { items }).then(r => r.data),
  riskLinksSuggest:  (id, raid, limit = 5) => client.post(`/projects/${id}/risk-links/suggest`, { raid, limit }).then(r => r.data),
  riskLinksSummary:  (id)      => client.get(`/projects/${id}/risk-links`).then(r => r.data),
  scheduleAtRisk:    ()        => client.get(`/portfolio/schedule-at-risk`).then(r => r.data),
  saveBaseline:      (id, name, tasks, milestones) => client.post(`/projects/${id}/baseline`, { name, tasks, milestones }).then(r => r.data),
  clearBaseline:     (id)      => client.delete(`/projects/${id}/baseline`).then(r => r.data),
};
